import type { Money } from '@/api/types';
import { fmtMoney } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Props {
  /** 保证金水平 % = equity / usedMargin × 100;usedMargin=0 → 传 null 表示 ∞(无持仓) */
  level: number | null;
  /** 强平阈值(量程下界) */
  low?: number;
  /** 量程上界 */
  high?: number;
  freeMargin?: Money | null;
  usedMargin?: Money | null;
  className?: string;
}

/** 按保证金水平分区取色:<100 危险 / <200 关注 / 其余健康 */
function zoneColor(level: number | null): string {
  if (level == null) return 'rgb(var(--c-up))';
  if (level < 100) return 'rgb(var(--c-down))';
  if (level < 200) return 'rgb(var(--c-warn))';
  return 'rgb(var(--c-up))';
}

/**
 * 半圆保证金仪表盘 — 红→黄→绿渐变弧 + 指针,参考设计图(强平阈值 50% 在左,量程上界在右)。
 * 保证金水平 = equity / 已用保证金 × 100(cTrader 口径);无持仓(已用=0)显示 ∞,指针指向最右(健康)。
 */
export function MarginGauge({
  level,
  low = 50,
  high = 500,
  freeMargin,
  usedMargin,
  className,
}: Props) {
  const W = 260;
  const H = 168;
  const cx = W / 2;
  const cy = 140; // 圆心靠下,半圆朝上
  const r = 104; // 弧半径
  const needleLen = r - 14;

  // level 归一化到 [0,1](low→0, high→1);∞ / 越界夹紧。
  const t =
    level == null ? 1 : Math.max(0, Math.min(1, (level - low) / (high - low)));

  // 半圆参数化:t=0→左端点(cx-r,cy),t=1→右端点(cx+r,cy),t=0.5→正上方。
  const angle = Math.PI * (1 - t);
  const needleX = cx + needleLen * Math.cos(angle);
  const needleY = cy - needleLen * Math.sin(angle);

  const color = zoneColor(level);
  const levelText = level == null ? '∞' : `${level.toFixed(0)}%`;

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="mx-auto w-full max-w-[300px]">
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="block">
        <defs>
          <linearGradient id="marginArc" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="rgb(var(--c-down))" />
            <stop offset="35%" stopColor="rgb(var(--c-warn))" />
            <stop offset="62%" stopColor="rgb(254 240 138)" />
            <stop offset="100%" stopColor="rgb(var(--c-up))" />
          </linearGradient>
        </defs>

        {/* 渐变弧(上半圆:左→上→右 = 顺时针 sweep-flag 1) */}
        <path
          d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
          fill="none"
          stroke="url(#marginArc)"
          strokeWidth={14}
          strokeLinecap="round"
        />

        {/* 中心读数 */}
        <text
          x={cx}
          y={cy - 34}
          fontSize="34"
          fontWeight="700"
          textAnchor="middle"
          fill={color}
          className="num"
        >
          {levelText}
        </text>

        {/* 指针 + 轴心 */}
        <line
          x1={cx}
          y1={cy}
          x2={needleX}
          y2={needleY}
          stroke="rgb(var(--c-ink-0))"
          strokeWidth={3}
          strokeLinecap="round"
        />
        <circle cx={cx} cy={cy} r={7} fill="rgb(var(--c-ink-0))" />
        <circle cx={cx} cy={cy} r={3} fill="rgb(var(--c-bg-2))" />

        {/* 刻度标签 */}
        <text x={cx - r} y={cy + 18} fontSize="11" textAnchor="middle" fill="rgb(var(--c-ink-3))" className="num">
          强平 {low}%
        </text>
        <text x={cx + r} y={cy + 18} fontSize="11" textAnchor="middle" fill="rgb(var(--c-ink-3))" className="num">
          {high}%
        </text>
      </svg>
      </div>

      {/* KV 行 */}
      <div className="mt-2 space-y-1.5 text-sm">
        <KvRow k="保证金水平" v={levelText} vClass="text-ink-0" />
        <KvRow k="可用保证金" v={fmtMoney(freeMargin)} vClass="text-ink-1" />
        <KvRow k="已用保证金" v={fmtMoney(usedMargin)} vClass="text-ink-1" />
      </div>
    </div>
  );
}

function KvRow({ k, v, vClass }: { k: string; v: string; vClass: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-3">{k}</span>
      <span className={`num font-semibold ${vClass}`}>{v}</span>
    </div>
  );
}
